import { createSlice } from "@reduxjs/toolkit"
import types from "../../Actions/types"
import { spousePreSurveyMetadata } from "./spousePreSurveyMetadata"
import { spouseApplicationMetadata } from "./spouseApplicationMetadata"
import { spouseProfileMetadata } from "./spouseProfileMetadata"
import { spousePostSurveyMetadata } from "./spousePostSurveyMetadata"
import * as utils from "../../Utils/reducerUtils"

const initialState = {
  showErrors: false,
  spouseApplicationMetadata,
  spouseProfileMetadata,
  profileMetadata: spouseProfileMetadata,
  spousePreSurveyMetadata,
  spousePostSurveyMetadata,
  initialized: false,
  submittingSpouseApplication: false,
  saving: false,
  application: {},
  applicationIsComplete: false,
  interests: [],
  fetchingInterests: false
}

const spouseSlice = createSlice({
  name: "spouseApplication",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.SUBMITTED_SPOUSE_APPLICATION, (state) => {
      state.submittingApplication = false
    })

    builder.addCase(types.LOGOUT, () => ({
      showErrors: false,
      spouseApplicationMetadata,
      spouseProfileMetadata,
      profileMetadata: spouseProfileMetadata,
      spousePreSurveyMetadata,
      spousePostSurveyMetadata,
      initialized: false
    }))

    builder.addCase(types.TOGGLE_SPOUSE_UNFILLED, (state) => {
      state.showErrors = true
      state.spouseApplicationMetadata = state.spouseApplicationMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        )
      }))
      state.spouseProfileMetadata = state.spouseProfileMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        )
      }))
    })

    builder.addCase(types.SUBMIT_SPOUSE_APPLICATION, (state) => {
      state.showErrors = false
      state.submittingSpouseApplication = true
    })

    builder.addCase(types.UPDATE_LIKERT_SPOUSE, (state, action) => {
      const { userField, value } = action.payload
      // Preserve existing behavior from legacy reducer
      if (state.spouseApplication && state.spouseApplication.application) {
        state.spouseApplication.application[userField] = value
      } else if (state.application) {
        // Fallback: update top-level application if present
        state.application[userField] = value
      }
    })

    builder.addCase(types.INIT_SPOUSE_APPLICATION, (state, action) => {
      const updatedMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.spouseApplicationMetadata
      )
      const updatedProfileMetaData = utils.processNavOptionsforProfile(
        action.data.application,
        state.spouseProfileMetadata
      )

      state.application = action.data.application
      state.spouseApplicationMetadata = updatedMetaData
      state.applicationIsComplete = utils.allFormsFilled(updatedMetaData)
      state.profileIsComplete = utils.allFormsFilled(updatedProfileMetaData)
      state.initialized = true
      state.profileMetadata = updatedProfileMetaData
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes"
    })

    builder.addCase(types.SET_SPOUSE_APPLICATION_INPUT_DATA, (state, action) => {
      state.currentInputData = action.data.inputData
    })

    builder.addCase(types.LOGIN, (state) => {
      state.initialized = false
      state.showErrors = false
    })

    builder.addCase(types.EDIT_APPLICATION, (state) => {
      state.currentInputData = null
      state.saving = false
    })

    builder.addCase(types.FETCHED_SPOUSE_APPLICATION, (state, action) => {
      const updatedApplicationMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.spouseApplicationMetadata
      )
      const updatedProfileData = utils.processNavOptionsforProfile(
        action.data.application,
        state.profileMetadata || state.spouseProfileMetadata
      )

      state.application = action.data.application
      state.spouseApplicationMetadata = updatedApplicationMetaData
      state.saving = false
      state.applicationIsComplete = utils.allFormsFilled(updatedApplicationMetaData)
      state.profileIsComplete = utils.allFormsFilled(updatedProfileData)
      state.profileMetadata = updatedProfileData
      state.currentInputData = null
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes"
    })

    builder.addCase(types.SAVING_SPOUSE_APPLICATION, (state) => {
      state.saving = true
    })

    builder.addCase(types.WILL_RELOCATE_SPOUSE_CHANGED, (state, action) => {
      const updatedMeta = utils.processNavOptionsForApplication(
        state.application,
        state.spouseApplicationMetadata
      )
      const updatedProfileMeta = utils.processNavOptionsforProfile(
        state.application,
        state.profileMetadata || state.spouseProfileMetadata
      )
      state.userWillReLocate = action.data.value
      state.applicationMetadata = updatedMeta
      state.profileMetadata = updatedProfileMeta
    })

    builder.addCase(types.UPDATE_INTERESTS, (state, action) => {
      state.interests = action.data.interests
    })

    builder.addCase(types.TOGGLE_FETCHING_INTERESTS, (state, action) => {
      state.fetchingInterests = action.data.toggle
    })

    builder.addDefaultCase((state) => state)
  }
})

export default spouseSlice.reducer
